/**
 * Domain types shared by server and client.
 *
 * Two rules from the plan are enforced by the shape of these types rather than
 * by discipline:
 *
 *  1. Descriptive aspects are never averaged into anything. `AspectKind`
 *     travels with every aspect and every session snapshot of one, so an
 *     aggregation function cannot reach a descriptive value without deciding
 *     what to do about it (see `shared/aggregate.ts`).
 *
 *  2. A session's taxonomy is frozen at session start. Nothing downstream reads
 *     `Aspect`; it reads `SessionAspect`, which carries its own labels. Editing
 *     the taxonomy therefore cannot retroactively rewrite an old review.
 */

export type AspectKind = "DESCRIPTIVE" | "EVALUATIVE";
export type Polarity = "BIPOLAR" | "UNIPOLAR";
export type SessionStatus = "DRAFT" | "COMPLETED";
export type DepthMode = "FULL" | "SIMPLE" | "KID";
export type CaptureMode = "SHARED_DEVICE" | "MULTI_DEVICE";
export type Medium = "PHYSICAL" | "DIGITAL";

/** Every syncable row carries these. */
export interface SyncMeta {
  id: string;
  owner_id: string;
  /** Bumped on every write; the basis for optimistic concurrency. */
  version: number;
  /** Monotonic per-owner cursor value. Pull returns rows above the client's cursor. */
  updated_seq: number;
  /** Tombstone. Rows are never physically removed — deletes must replicate. */
  deleted: 0 | 1;
  updated_at: number;
}

export interface Profile extends SyncMeta {
  name: string;
  colour: string;
  avatar: string | null;
  depth_mode: DepthMode;
  /** Ephemeral raters created at the table; promoted to a real profile on request. */
  is_guest: 0 | 1;
  archived_at: number | null;
}

export interface Game extends SyncMeta {
  /** Nullable on purpose: rating must never block on BGG being reachable. */
  bgg_id: number | null;
  title: string;
  year: number | null;
  image_url: string | null;
  thumb_url: string | null;
  min_players: number | null;
  max_players: number | null;
  playtime: number | null;
  weight: number | null;
  /** True when the game was typed in by hand rather than resolved from BGG. */
  manual: 0 | 1;
  cached_at: number | null;
}

/** A labelled stop on a rating track. The label names a state, not a magnitude. */
export interface Stop {
  value: number;
  label: string;
}

export interface Aspect extends SyncMeta {
  name: string;
  kind: AspectKind;
  polarity: Polarity;
  /** Asked once per game rather than once per session (e.g. Components). */
  stable: 0 | 1;
  /** Part of the default active set offered at session start. */
  starred: 0 | 1;
  rubric_version: number;
  stops: Stop[];
  sort: number;
  archived_at: number | null;
  /**
   * The shipped base set's identifier for this question, or null when somebody
   * wrote it themselves. Only a rendering hint — `shared/catalog.ts` turns it
   * into the reader's language and falls back to `name` when it cannot.
   */
  seed_key: string | null;
}

export interface SubQuestion extends SyncMeta {
  aspect_id: string;
  name: string;
  stops: Stop[];
  sort: number;
  archived_at: number | null;
  seed_key: string | null;
}

export interface PlaySession extends SyncMeta {
  game_id: string;
  status: SessionStatus;
  capture_mode: CaptureMode;
  played_at: number;
  note: string | null;
  /** One-line group verdict; the feed's hero line. */
  verdict: string | null;
  player_count: number | null;
  first_play: 0 | 1;
  taught_by: string | null;
  chosen_by: string | null;
  expansions: string | null;
  medium: Medium;
  rubric_version: number;
  /** Present only while the session accepts joins. */
  join_code: string | null;
  join_expires_at: number | null;
}

export interface SessionParticipant extends SyncMeta {
  session_id: string;
  profile_id: string;
  is_guest: 0 | 1;
  name_snapshot: string;
  colour_snapshot: string;
  /** Set when a device claims this participant via a QR join. */
  claimed_at: number | null;
  /** Last time this participant's device was seen; drives "waiting on Lea". */
  seen_at: number | null;
}

export interface SessionAspect extends SyncMeta {
  session_id: string;
  aspect_id: string;
  name_snapshot: string;
  kind_snapshot: AspectKind;
  polarity_snapshot: Polarity;
  stops_snapshot: Stop[];
  sort_snapshot: number;
  /** Frozen with the rest: the live aspect may be archived by the time this is read. */
  seed_key_snapshot: string | null;
  /** Set when every present participant has committed. Drives the reveal. */
  revealed_at: number | null;
}

export interface SessionSubQuestion extends SyncMeta {
  session_id: string;
  session_aspect_id: string;
  subquestion_id: string;
  name_snapshot: string;
  stops_snapshot: Stop[];
  sort_snapshot: number;
  seed_key_snapshot: string | null;
  revealed_at: number | null;
}

/** The one mandatory number. Never derived from aspects. */
export interface HeadlineRating extends SyncMeta {
  session_id: string;
  participant_id: string;
  /** 1..10, "desire to play again". */
  value: number;
}

/**
 * One person's overall for the night — but only when they overrode it.
 *
 * The overall is the mean of that seat's topic values (`overallValue` in
 * `shared/aggregate.ts`), so the ordinary night writes no row here at all. A row
 * exists only where somebody armed the stars beside the headline and moved them,
 * which is why this table is the override and not the value: storing the derived
 * figure as well would create a second copy that stops agreeing with the topics
 * the moment one of them is corrected, and the whole point of deriving it is that
 * it cannot.
 *
 * Distinct from the headline, which sits next to it and is a different question.
 * The headline is "do you want to play this again"; this is "how good was the
 * night". A group can badly want a rematch at a game they rated middling.
 */
export interface OverallRating extends SyncMeta {
  session_id: string;
  participant_id: string;
  /** 1..10 half-stars, like every other rating in the app. */
  value: number;
}

export interface AspectRating extends SyncMeta {
  session_id: string;
  participant_id: string;
  session_aspect_id: string;
  /** Null when `na` is set. Signed for bipolar axes, centre = 0. */
  value: number | null;
  na: 0 | 1;
  note: string | null;
}

export interface SubQuestionRating extends SyncMeta {
  session_id: string;
  participant_id: string;
  session_subquestion_id: string;
  value: number | null;
  na: 0 | 1;
  note: string | null;
}

/** Binary "would play again" heart, orthogonal to the score. */
export interface PlayAgain extends SyncMeta {
  profile_id: string;
  game_id: string;
  value: 0 | 1;
}

// ---------------------------------------------------------------------------
// Sync envelope
// ---------------------------------------------------------------------------

export const SYNCABLE = [
  "profile",
  "game",
  "aspect",
  "subquestion",
  "play_session",
  "session_participant",
  "session_aspect",
  "session_subquestion",
  "headline_rating",
  "overall_rating",
  "aspect_rating",
  "subquestion_rating",
  "play_again",
] as const;

export type Entity = (typeof SYNCABLE)[number];

export interface Mutation {
  /** Deduplication key. A retry carries the same op_id and is applied once. */
  op_id: string;
  entity: Entity;
  id: string;
  /**
   * Version the client believed it was editing. 0 means "this is a create".
   * A mismatch is surfaced as a conflict, never silently resolved.
   */
  base_version: number;
  /** Field values to write. Absent fields are left untouched. */
  patch: Record<string, unknown>;
  deleted?: boolean;
}

export type MutationResult =
  | { op_id: string; status: "applied"; version: number; updated_seq: number }
  | { op_id: string; status: "duplicate"; version: number; updated_seq: number }
  | { op_id: string; status: "conflict"; server_version: number; server_row: Record<string, unknown> }
  | { op_id: string; status: "rejected"; reason: string };

export interface PushRequest {
  mutations: Mutation[];
}

/**
 * Deliberately carries no cursor.
 *
 * A push cursor is the owner's *global* sequence, which includes rows this
 * device has never pulled. Adopting it as the pull cursor would step the client
 * past another device's writes permanently. Per-mutation `updated_seq` values in
 * `results` are the device's own rows and are safe.
 */
export interface PushResponse {
  results: MutationResult[];
}

export interface PullResponse {
  cursor: number;
  /** Upserts and tombstones, keyed by entity. */
  changes: Partial<Record<Entity, Record<string, unknown>[]>>;
}
